import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  TextInput,
  ScrollView,
  Dimensions,
  TouchableOpacity,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { useSelector } from 'react-redux';
import { createApiClient } from '../../../../api/apiClient';
import DLOButton from '../DLOButton';
import DLOModal from '../DLOModal';
import DLOTooltip from '../DLOTooltip';
import { formatNumberWithSeparators } from '../../utils/numberFormatUtils';
import { useTheme } from '../../../../theme/useTheme';
import type { RootState } from '../../../../store';

// Action Icons from Figma
const HistoryIcon = ({ color = '#3A3A3A' }) => (
  <Svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <Path
      d="M10.8335 2.5C6.69183 2.5 3.3335 5.85833 3.3335 10H0.833496L4.07516 13.2417L4.1335 13.3583L7.50016 10H5.00016C5.00016 6.775 7.6085 4.16667 10.8335 4.16667C14.0585 4.16667 16.6668 6.775 16.6668 10C16.6668 13.225 14.0585 15.8333 10.8335 15.8333C9.22516 15.8333 7.76683 15.175 6.71683 14.1167L5.5335 15.3C6.89183 16.6583 8.7585 17.5 10.8335 17.5C14.9752 17.5 18.3335 14.1417 18.3335 10C18.3335 5.85833 14.9752 2.5 10.8335 2.5ZM10.0002 6.66667V10.8333L13.5418 12.9333L14.1835 11.8667L11.2502 10.125V6.66667H10.0002Z"
      fill={color}
    />
  </Svg>
);

interface PercentageRow {
  id: string;
  percentages: {
    sun: { default: string; override: string };
    mon: { default: string; override: string };
    tue: { default: string; override: string };
    wed: { default: string; override: string };
    thu: { default: string; override: string };
    fri: { default: string; override: string };
    sat: { default: string; override: string };
  };
  total: string;
  __focused?: { [key: string]: boolean }; // Track focused state for UI
  __changed?: { [key: string]: boolean }; // Track changed override values for bold styling
  __actualOverrides?: { [key: string]: boolean }; // Track which days have backend overrides
}

export interface DLOPercentageOfWeeklyDSDVolumeTableProps {
  data?: PercentageRow[];
  value?: PercentageRow[]; // Current controlled value from parent
  onChange?: (data: PercentageRow[]) => void; // Callback when data changes
  onSave?: (updates?: any[]) => void; // Modified to accept optional prepared updates array
  onRowSave?: (rowId: string) => void;
  onRowRestoreDefaults?: (rowId: string) => void; // Added for restore defaults functionality
  headerTitle?: string; // Added headerTitle prop
  style?: any;
  type?: 'DSD' | 'NON-DSD'; // Type prop for API calls
  context?: 'location-settings' | 'schedule-settings'; // Context to distinguish usage
  isLoading?: boolean; // Loading prop for save button
  displayAsPercentage?: boolean; // Control whether values are displayed/formatted as percentages
  disabled?: boolean; // If true, make all inputs and actions read-only/disabled
}

const DLOPercentageOfWeeklyDSDVolumeTable: React.FC<DLOPercentageOfWeeklyDSDVolumeTableProps> = ({
  data = [],
  value,
  onChange,
  onSave,
  onRowSave,
  onRowRestoreDefaults,
  headerTitle = "PERCENTAGE OF WEEKLY DSD VOLUME",
  style,
  type = 'DSD',
  context = 'location-settings',
  isLoading = false,
  displayAsPercentage = true,
  disabled = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';


  // Redux selectors
  const selectedLocation = useSelector((state: RootState) => state.appData.selectedLocation);
  const period = useSelector((state: RootState) => state.appData.periodWeek?.data?.period);
  const week = useSelector((state: RootState) => state.appData.periodWeek?.data?.week);
  
  // Local state for modal and loading
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedRows, setSavedRows] = useState<Set<string>>(new Set());
  const [savedBaselines, setSavedBaselines] = useState<Map<string, PercentageRow['percentages']>>(new Map());
  
  // Track which days have backend overrides (from API data) - only used in schedule-settings
  const [daysWithBackendOverrides, setDaysWithBackendOverrides] = useState<Set<string>>(new Set());
  
  // Calculate total from override values only
  const calculateTotal = (percentages: PercentageRow['percentages']) => {
    let total = 0;
    Object.values(percentages).forEach(day => {
      const cleanValue = displayAsPercentage ? day.override.replace('%', '') : day.override;
      const overrideValue = parseFloat(cleanValue);
      total += isNaN(overrideValue) ? 0 : overrideValue;
    });
    
    if (displayAsPercentage) {
      return `${total.toFixed(2)}%`;
    } else {
      // Apply thousand separators for non-percentage display
      return formatNumberWithSeparators(total.toFixed(2));
    }
  };

  // Initialize data with proper format based on displayAsPercentage
  const initializeData = () => {
    if (data.length > 0) {
      return data.map(row => ({
        ...row,
        total: calculateTotal(row.percentages)
      }));
    }
    
    return [
      {
        id: '1',
        percentages: {
          sun: { default: displayAsPercentage ? '0%' : '0', override: displayAsPercentage ? '0%' : '0' },
          mon: { default: displayAsPercentage ? '0%' : '0', override: displayAsPercentage ? '0%' : '0' },
          tue: { default: displayAsPercentage ? '0%' : '0', override: displayAsPercentage ? '0%' : '0' },
          wed: { default: displayAsPercentage ? '0%' : '0', override: displayAsPercentage ? '0%' : '0' },
          thu: { default: displayAsPercentage ? '0%' : '0', override: displayAsPercentage ? '0%' : '0' },
          fri: { default: displayAsPercentage ? '0%' : '0', override: displayAsPercentage ? '0%' : '0' },
          sat: { default: displayAsPercentage ? '0%' : '0', override: displayAsPercentage ? '0%' : '0' },
        },
        total: displayAsPercentage ? '0.00%' : '0.00',
      },
    ];
  };

  const [percentageData, setPercentageData] = useState<PercentageRow[]>(() => initializeData());

  const [hoveredButton, setHoveredButton] = useState<string | null>(null);
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  
  // Ref to track if we're currently saving to prevent data reset
  const isSavingRef = useRef(false);
  const initialDataRef = useRef<string>('');

  // Update screen dimensions on change
  useEffect(() => {
    const onChange = (result: any) => {
      setScreenData(result.window);
    };

    const subscription = Dimensions.addEventListener('change', onChange);
    return () => subscription?.remove();
  }, []);

  // Update percentageData when data prop changes and recalculate totals
  // Only update if data actually changed (not just reference) and we're not currently saving
  useEffect(() => {
    // In controlled mode (schedule-settings), use value prop instead of data prop
    const sourceData = context === 'schedule-settings' && value !== undefined ? value : data;
    
    if (sourceData.length > 0) {
      const newDataString = JSON.stringify(sourceData);
      
      // Only update if this is the first load or if the data content has actually changed
      if (initialDataRef.current === '' || (initialDataRef.current !== newDataString && !isSavingRef.current)) {
        initialDataRef.current = newDataString;
        setPercentageData(sourceData.map(row => ({
          ...row,
          total: calculateTotal(row.percentages)
        })));
        
        // Detect backend overrides from __actualOverrides metadata
        if (sourceData[0]?.__actualOverrides) {
          const backendOverrideDays = new Set<string>();
          const actualOverrides = sourceData[0].__actualOverrides;
          
          Object.entries(actualOverrides).forEach(([day, hasOverride]) => {
            if (hasOverride) {
              backendOverrideDays.add(day);
            }
          });
          
          setDaysWithBackendOverrides(backendOverrideDays);
        }
        
        // Reset saved states when data changes from external source
        if (initialDataRef.current !== '') {
          setSavedRows(new Set());
        }
      }
    }
  }, [data, value, context]);

  const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helper function to format values based on displayAsPercentage prop
  const formatValue = (value: string): string => {
    if (!value || value === '') return '';
    
    if (!displayAsPercentage) {
      // For non-percentage (volume) mode, strip commas and return a clean integer string
      const numericValue = value.replace(/[^0-9.]/g, '').trim();
      if (!numericValue) return '';
      const parsed = parseFloat(numericValue);
      if (isNaN(parsed)) return value;
      // Return as integer string (no decimal places for volume counts)
      return String(Math.round(parsed));
    }
    
    // For percentage mode (existing logic)
    let numericValue = value.replace(/[^0-9.]/g, '').trim();
    if (!numericValue) return '';
    const parsed = parseFloat(numericValue);
    if (isNaN(parsed)) return value;
    return `${parsed.toFixed(2)}%`;
  };

  // Helper function to validate decimal input
  const validateDecimalInput = (value: string): boolean => {
    if (value === '') return true;
    
    // Strip commas and % before validating to allow comma-separated large numbers
    let cleanValue = value.replace(/,/g, '');
    if (displayAsPercentage) {
      cleanValue = cleanValue.replace('%', '');
    }
    
    // Allow decimal numbers with appropriate precision based on display mode
    // For percentage mode: up to 2 decimal places (e.g., 99.99%)
    // For non-percentage mode: more flexible to allow longer numbers (e.g., 4666, 12345.67)
    const decimalRegex = displayAsPercentage 
      ? /^\d*\.?\d{0,2}$/ 
      : /^\d*\.?\d*$/;  // Allow any number of digits for non-percentage mode
    return decimalRegex.test(cleanValue);
  };

  // Helper function to check if total value is correct
  const isTotalCorrect = (total: string): boolean => {
    if (displayAsPercentage) {
      return total === '100%' || total === '100.00%';
    } else {
      // For non-percentage mode, any valid total is acceptable
      return true;
    }
  };

  const updatePercentage = (rowId: string, day: keyof PercentageRow['percentages'], type: 'default' | 'override', value: string) => {
    // Only validate override inputs for decimal format
    if (type === 'override' && !validateDecimalInput(value)) {
      return; // Don't update if invalid input
    }
    // Strip commas so the stored value is a plain number string
    if (!displayAsPercentage) {
      value = value.replace(/,/g, '');
    }

    const newData = percentageData.map(row => {
      if (row.id === rowId) {
        // Only track changes for override fields when the value is actually being modified
        let changed = row.__changed || {};
        
        if (type === 'override') {
          // Normalize values for comparison (remove % and format)
          const normalizeValue = (val: string) => {
            const cleaned = displayAsPercentage ? val.replace('%', '').trim() : val.trim();
            const parsed = parseFloat(cleaned);
            return isNaN(parsed) ? 0 : parsed;
          };
          
          // Determine the baseline to compare against
          let baseline: string;
          if (savedRows.has(rowId)) {
            // After save: compare to the saved baseline
            const savedBaseline = savedBaselines.get(rowId);
            baseline = savedBaseline ? savedBaseline[day].override : row.percentages[day].override;
          } else {
            // Before save: compare to default
            baseline = row.percentages[day].default;
          }
          
          // Use normalized comparison to detect actual changes
          const normalizedValue = normalizeValue(value);
          const normalizedBaseline = normalizeValue(baseline);
          const isDifferentFromBaseline = normalizedValue !== normalizedBaseline;
          
          // Only mark as changed if value differs from baseline
          changed = { ...changed, [day]: isDifferentFromBaseline };
        }
        
        const updatedRow = {
          ...row,
          percentages: {
            ...row.percentages,
            [day]: {
              ...row.percentages[day],
              [type]: value,
            },
          },
          __changed: changed,
        };
        // Recalculate total using only override values
        updatedRow.total = calculateTotal(updatedRow.percentages);
        return updatedRow;
      }
      return row;
    });

    setPercentageData(newData);
    // Reset saved state for this row when data changes (only for override changes)
    if (type === 'override') {
      setSavedRows(prev => {
        const newSavedRows = new Set(prev);
        newSavedRows.delete(rowId);
        return newSavedRows;
      });
    }
    // If in controlled mode (schedule-settings), call onChange to update parent state
    if (context === 'schedule-settings' && onChange) {
      onChange(newData);
    }
  };

  // Handle value formatting on blur (only for override fields)
  const handleValueBlur = (rowId: string, day: keyof PercentageRow['percentages'], type: 'default' | 'override', value: string) => {
    if (type === 'override') {
      const formattedValue = formatValue(value);
      if (formattedValue !== value) {
        updatePercentage(rowId, day, type, formattedValue);
      }
    }
  };

  const isSaveDisabled = (row: PercentageRow) => {
    // Check if all 7 override values are present (not empty)
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
    
    // Only check if any override values are missing or empty (allow zero values)
    const hasEmptyValues = days.some(day => {
      const overrideValue = row.percentages[day].override;
      // Only consider truly empty values as not present (allow 0%, 0.00%, 0, etc.)
      return !overrideValue || overrideValue.trim() === '';
    });
    
    if (hasEmptyValues) {
      return true; // Disable if any values are missing/empty
    }
    
    // Check if user has actually modified any days (applies to BOTH contexts)
    // Use __changed object to track user modifications
    const hasAnyChanges = row.__changed && Object.values(row.__changed).some(isChanged => isChanged === true);
    
    if (!hasAnyChanges) {
      // No modifications in this session - disable save
      return true;
    }
    
    // Check total validation only if we have modifications
    if (displayAsPercentage) {
      // For percentage mode, check if the total equals exactly 100%
      let total = 0;
      days.forEach(day => {
        const overrideValue = row.percentages[day].override;
        const numericValue = parseFloat(overrideValue.replace('%', ''));
        total += isNaN(numericValue) ? 0 : numericValue;
      });
      
      // If total is not 100%, disable save
      if (total !== 100) {
        return true;
      }
    } else {
      // For non-percentage mode, just check if all fields have valid numeric values
      const hasInvalidValues = days.some(day => {
        const overrideValue = row.percentages[day].override;
        const numericValue = parseFloat(overrideValue);
        return isNaN(numericValue);
      });
      
      if (hasInvalidValues) {
        return true;
      }
    }
    
    // All checks passed - enable save
    return false;
  };

  // Save daily percentage function
  const handleSave = async (row: PercentageRow) => {
    if (!selectedLocation?.id || !period || !week) {
      setModalTitle('Error');
      setModalMessage('Missing required location, period, or week information');
      setIsSuccess(false);
      setIsModalVisible(true);
      return;
    }

    setIsSaving(true);

    try {
      const api = createApiClient();
      
      // Transform data for API call
      const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const dayMapping = { sun: 1, mon: 2, tue: 3, wed: 4, thu: 5, fri: 6, sat: 7 };
      
      const dailyUpdates = days.map(day => {
        const overrideValue = row.percentages[day as keyof typeof row.percentages].override;
        let numericValue: number;
        
        if (displayAsPercentage) {
          // For percentage mode: remove % symbol and divide by 100
          numericValue = parseFloat(overrideValue.replace('%', '')) / 100;
        } else {
          // For non-percentage mode: use value as-is (assuming it's already a decimal)
          numericValue = parseFloat(overrideValue);
        }
        
        return {
          dayNum: dayMapping[day as keyof typeof dayMapping],
          override_daily_pct: parseFloat(numericValue.toFixed(4))
        };
      });

      const requestBody = {
        loc_id: selectedLocation.id,
        dailyUpdates: dailyUpdates
      };

      const response = await api.put(`/dlo/LocationSettings/Daily?type=${type}`, requestBody);

      // Success
      setModalTitle('Success');
      setModalMessage(displayAsPercentage 
        ? 'Daily percentage settings have been saved successfully' 
        : 'Daily volume settings have been saved successfully');
      setIsSuccess(true);
      setIsModalVisible(true);
      
      // Clear __changed immediately and mark row as saved
      setPercentageData(prev => {
        return prev.map(r => {
          if (r.id === row.id) {
            return { ...r, __changed: {} };
          }
          return r;
        });
      });
      
      // Then set savedRows
      setSavedRows(prev => new Set(prev).add(row.id));
      
      // Call parent onRowSave if provided
      onRowSave?.(row.id);

    } catch (error: any) {
      // Error
      setModalTitle('Error');
      setModalMessage(error?.response?.data?.message || error?.message || 'An error occurred while saving');
      setIsSuccess(false);
      setIsModalVisible(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestoreDefaults = (rowId: string) => {
    const updatedData = percentageData.map(row => {
      if (row.id === rowId) {
        // Create new percentages object with default values copied to override fields
        const updatedPercentages = { ...row.percentages };
        const changed: { [key: string]: boolean } = {};

        Object.keys(updatedPercentages).forEach(day => {
          const dayKey = day as keyof PercentageRow['percentages'];
          const defaultValue = updatedPercentages[dayKey].default;
          const overrideValue = updatedPercentages[dayKey].override;

          if (defaultValue !== overrideValue) {
            changed[dayKey] = true;
          }

          updatedPercentages[dayKey] = {
            ...updatedPercentages[dayKey],
            override: defaultValue
          };
        });

        // Calculate new total
        const newTotal = calculateTotal(updatedPercentages);

        return {
          ...row,
          percentages: updatedPercentages,
          total: newTotal,
          __changed: changed,
        };
      }
      return row;
    });

    setPercentageData(updatedData);
    onChange?.(updatedData);
    onRowRestoreDefaults?.(rowId);
    
    // Reset saved state for this row since data has changed
    setSavedRows(prev => {
      const newSavedRows = new Set(prev);
      newSavedRows.delete(rowId);
      return newSavedRows;
    });
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    if (isSuccess) {
      // Additional success actions can be added here
    }
  };

  // Get responsive styles
  const getResponsiveStyles = () => {
    // Fixed styles for all screen sizes - rely on horizontal scrolling instead of resizing
    return {
      containerMinWidth: 1200, // Fixed container width
      dayGroupMinWidth: 120,   // Fixed day group width
      inputWidth: 75,          // Fixed input width (no resizing)
      inputHeight: 32,         // Fixed input height
      headerHeight: 48,        // Fixed header height
      dataRowHeight: 48,       // Fixed data row height
      actionMinWidth: 200,     // Fixed action column width
      fontSize: {
        header: 12,            // Changed from 14 to 12px
        subHeader: 12,
        input: 12,
        total: 11,
      },
      padding: {
        horizontal: 8,         // Fixed padding
        vertical: 8,
      },
      gap: 8,                  // Fixed gap
      buttonSize: 'small',
      stackActionItems: false, // Never stack items - use horizontal scrolling instead
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <ScrollView 
      horizontal 
      showsHorizontalScrollIndicator={Platform.OS === 'web'}
      contentContainerStyle={styles.scrollContainer}
    >
      <View style={[
        styles.container, 
        { minWidth: responsiveStyles.containerMinWidth },
        style
      ]}>
        {/* Main Header */}
        <View style={[styles.mainHeader, { height: responsiveStyles.headerHeight }]}>
          <View style={styles.percentageVolumeSection}>
            <Text style={[
              styles.headerSectionText,
              { fontSize: responsiveStyles.fontSize.header }
            ]}>
              {headerTitle}
            </Text>
          </View>

          <View style={styles.headerDivider} />

          <View style={[
            styles.actionSection,
            { minWidth: responsiveStyles.actionMinWidth }
          ]}>
            <Text style={[
              styles.headerSectionText,
              { fontSize: responsiveStyles.fontSize.header }
            ]}>
              ACTION
            </Text>
          </View>
        </View>

        {/* Sub Header */}
        <View style={[
          styles.subHeader,
          { minHeight: responsiveStyles.headerHeight * 1.5 }
        ]}>
          {/* Days with Default/Override columns */}
          <View style={styles.daysGroup}>
            {dayLabels.map((day, index) => (
              <React.Fragment key={`day-${day}`}>
                {index > 0 && <View style={styles.dayColumnDivider} />}
                <View style={[
                  styles.dayGroup,
                  { minWidth: responsiveStyles.dayGroupMinWidth }
                ]}>
                  <View style={[
                    styles.dayHeader,
                    { height: responsiveStyles.headerHeight * 0.8 }
                  ]}>
                    <Text style={[
                      styles.dayHeaderText,
                      { fontSize: responsiveStyles.fontSize.subHeader }
                    ]}>
                      {day}
                    </Text>
                  </View>
                  <View style={styles.daySubColumns}>
                    <View style={styles.subColumn}>
                      <Text style={[
                        styles.subHeaderText,
                        { fontSize: responsiveStyles.fontSize.subHeader - 1 }
                      ]}>
                        Default
                      </Text>
                    </View>
                    <View style={styles.subColumnDivider} />
                    <View style={styles.subColumn}>
                      <Text style={[
                        styles.subHeaderText,
                        { fontSize: responsiveStyles.fontSize.subHeader - 1 }
                      ]}>
                        Override
                      </Text>
                    </View>
                  </View>
                </View>
              </React.Fragment>
            ))}
          </View>

          <View style={styles.subColumnDivider} />

          {/* Combined Total and Action Column */}
          <View style={[
            styles.totalActionGroup,
            { minWidth: responsiveStyles.actionMinWidth }
          ]}>
            <View style={[
              styles.actionHeader,
              { height: responsiveStyles.headerHeight * 0.8 }
            ]}>
              <Text style={styles.dayHeaderText}></Text>
            </View>
          </View>
        </View>

        {/* Data Rows */}
        {percentageData.map((row, rowIndex) => (
          <View key={row.id} style={[
            styles.dataRow,
            { minHeight: responsiveStyles.dataRowHeight },
            rowIndex === percentageData.length - 1 && styles.lastDataRow
          ]}>
            {/* Days with Default/Override inputs */}
            <View style={styles.daysGroup}>
              {dayLabels.map((day, index) => {
                const dayKey = day.toLowerCase() as keyof PercentageRow['percentages'];
                return (
                  <React.Fragment key={`day-input-${day}`}>
                    {index > 0 && <View style={styles.inputColumnDivider} />}
                    <View style={[
                      styles.dayGroup,
                      { minWidth: responsiveStyles.dayGroupMinWidth }
                    ]}>
                      <View style={styles.dayInputsContainer}>
                        <View style={styles.subColumn}>
                          <View style={styles.inputContainer}>
                            <TextInput
                              id={`default-${row.id}-${dayKey}`}
                              style={[
                                styles.input,
                                styles.defaultInput,
                                {
                                  width: responsiveStyles.inputWidth,
                                  height: responsiveStyles.inputHeight,
                                  fontSize: responsiveStyles.fontSize.input,
                                }
                              ]}
                              value={displayAsPercentage ? row.percentages[dayKey].default : formatNumberWithSeparators(row.percentages[dayKey].default)}
                              onChangeText={(value) => updatePercentage(row.id, dayKey, 'default', value)}
                              placeholder={displayAsPercentage ? "0%" : "0"}
                              placeholderTextColor="#999"
                              editable={false}
                              pointerEvents="none"
                            />
                          </View>
                        </View>
                        <View style={styles.subColumnDivider} />
                        <View style={styles.subColumn}>
                          <View style={styles.inputContainer}>
                            <TextInput
                              id={`override-${row.id}-${dayKey}`}
                              style={[
                                styles.input,
                                styles.overrideInput,
                                // Only bold while entering (focused)
                                row.__changed && row.__changed[dayKey] ? { fontWeight: 'bold' } : { fontWeight: '400' },
                                {
                                  width: responsiveStyles.inputWidth,
                                  height: responsiveStyles.inputHeight,
                                  fontSize: responsiveStyles.fontSize.input,
                                  backgroundColor: disabled ? '#f0f0f0' : undefined,
                                },
                                isDark && {
                                  backgroundColor: disabled ? '#1A1A1A' : colors.background.elevated,
                                  color: disabled ? colors.text.secondary : colors.text.primary,
                                  borderColor: colors.border.default,
                                },
                              ]}
                              value={displayAsPercentage 
                                ? row.percentages[dayKey].override 
                                : formatNumberWithSeparators(row.percentages[dayKey].override)
                              }
                              onFocus={disabled ? undefined : () => {
                                setPercentageData(prev => prev.map(r => r.id === row.id ? { ...r, __focused: { ...(r.__focused || {}), [dayKey]: true } } : r));
                              }}
                              onBlur={disabled ? undefined : () => {
                                handleValueBlur(row.id, dayKey, 'override', row.percentages[dayKey].override);
                                setPercentageData(prev => prev.map(r => r.id === row.id ? { ...r, __focused: { ...(r.__focused || {}), [dayKey]: false } } : r));
                              }}
                              onChangeText={disabled ? undefined : (value) => updatePercentage(row.id, dayKey, 'override', value)}
                              placeholder={displayAsPercentage ? "0.00%" : "0.00"}
                              placeholderTextColor="#999"
                              keyboardType="decimal-pad"
                              editable={!disabled}
                              pointerEvents={disabled ? 'none' : undefined}
                            />
                          </View>
                        </View>
                      </View>
                    </View>
                  </React.Fragment>
                );
              })}
            </View>

            <View style={styles.inputColumnDivider} />

            {/* Combined Total and Action Column */}
            <View style={[
              styles.totalActionGroup, styles.totalActionGroupData,
              { minWidth: responsiveStyles.actionMinWidth }
            ]}>
              <View style={[
                styles.totalSection,
                {
                  paddingHorizontal: responsiveStyles.padding.horizontal,
                  paddingVertical: responsiveStyles.padding.vertical,
                  gap: responsiveStyles.gap / 3, // Further reduced gap for more compactness
                }
              ]}>
                <Text style={[
                  styles.totalLabelText,
                  { 
                    fontSize: 13, // Fixed 13px font size
                    marginBottom: 6, // Increased margin bottom
                  }
                ]}>
                  Total
                </Text>
                <View style={[
                  styles.overrideSaveRow,
                  {
                    // gap: 5, // Set to 5px gap
                    flexDirection: responsiveStyles.stackActionItems ? 'column' : 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: responsiveStyles.stackActionItems ? 'nowrap' : 'wrap',
                    minHeight: responsiveStyles.stackActionItems ? 38 : 'auto', // Reduced min height
                  }
                ]}>
                  <View style={[
                    styles.totalBadge,
                    isTotalCorrect(row.total) ? styles.totalBadgeGreen : styles.totalBadgeRed,
                    {
                      marginBottom: responsiveStyles.stackActionItems ? 2 : 0, // Further reduced margin
                      maxWidth: responsiveStyles.stackActionItems ? '100%' : 'auto',
                      paddingHorizontal: 8, // Set to 8px for all screen sizes
                      paddingVertical: 8, // Set to 8px for all screen sizes
                    }
                  ]}>
                    <Text 
                      style={[
                        styles.totalBadgeText,
                        isTotalCorrect(row.total) ? styles.totalBadgeTextGreen : styles.totalBadgeTextRed,
                        { 
                          fontSize: responsiveStyles.fontSize.total,
                          lineHeight: responsiveStyles.fontSize.total * 1.1, // Reduced line height
                        }
                      ]}
                      numberOfLines={1}
                      ellipsizeMode="tail"
                    >
                      {row.total}
                    </Text>
                  </View>
                  <DLOButton
                    text={savedRows.has(row.id) ? "Saved" : "Save"}
                    onPress={disabled ? undefined : () => {
                      isSavingRef.current = true;
                      if (context === 'schedule-settings' && onSave) {
                        // Prepare updates array - send ALL 7 days with their current override values
                        const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
                        const dayMapping = { sun: 1, mon: 2, tue: 3, wed: 4, thu: 5, fri: 6, sat: 7 };
                        
                        // When user modifies ANY day, send ALL override values as they are
                        const updates = days.map(day => {
                          const dayData = row.percentages[day as keyof typeof row.percentages];
                          
                          // Convert override value to decimal format
                          let override_daily_pct: number;
                          
                            let value: number;
                            if (displayAsPercentage) {
                            // Remove % and convert to decimal (e.g., "20.00%" -> 0.2)
                            value = parseFloat(dayData.override.replace('%', '')) / 100;
                            return {
                              dayNum: dayMapping[day as keyof typeof dayMapping],
                              override_daily_pct: value
                            };
                            } else {
                            // Already in decimal format
                            value = parseFloat(dayData.override);
                            return {
                              dayNum: dayMapping[day as keyof typeof dayMapping],
                              override_daily_cases: value
                            };
                            }
                        });
                        
                        // Pass prepared updates to parent handler
                        onSave(updates);
                        
                        // Save the current override values as the new baseline
                        const currentRow = percentageData.find(r => r.id === row.id);
                        if (currentRow) {
                          setSavedBaselines(prev => {
                            const newBaselines = new Map(prev);
                            newBaselines.set(row.id, { ...currentRow.percentages });
                            return newBaselines;
                          });
                        }
                        
                        // Clear __changed for this row immediately after calling parent's onSave
                        setPercentageData(prev => prev.map(r => 
                          r.id === row.id ? { ...r, __changed: {} } : r
                        ));
                        
                        // Update daysWithBackendOverrides to include the newly saved days
                        if (row.__changed) {
                          const newlyModifiedDays = Object.entries(row.__changed)
                            .filter(([_, isChanged]) => isChanged)
                            .map(([day, _]) => day);
                          setDaysWithBackendOverrides(prev => new Set([...prev, ...newlyModifiedDays]));
                        }
                        
                        // Mark row as saved
                        setSavedRows(prev => new Set(prev).add(row.id));
                      } else {
                        handleSave(row);
                      }
                      setTimeout(() => {
                        isSavingRef.current = false;
                      }, 2000);
                    }}
                    size={responsiveStyles.buttonSize as 'small'}
                    variant="primary"
                    style={{ height: 32, backgroundColor: '#005CBC' }}
                    disabled={disabled || isSaveDisabled(row) || savedRows.has(row.id)}
                    isLoading={context === 'schedule-settings' ? isLoading : isSaving}
                  />
                  <DLOTooltip 
                    content="Defaults" 
                    position="top" 
                    trigger="hover" 
                    verticalOffset={30}
                    horizontalOffset={-10}
                    maxWidth={80}
                    minWidth={100}
                    padding={6}
                    textAlign="center"
                  >
                    <TouchableOpacity
                      onPress={disabled ? undefined : () => handleRestoreDefaults(row.id)}
                      style={[
                        styles.actionButton,
                        {
                          backgroundColor: hoveredButton === `defaults-${row.id}` ? '#F5F5F5' : 'transparent',
                          opacity: disabled ? 0.5 : 1,
                        }
                      ]}
                      onPressIn={disabled ? undefined : () => setHoveredButton(`defaults-${row.id}`)}
                      onPressOut={disabled ? undefined : () => setHoveredButton(null)}
                      disabled={disabled}
                    >
                      <HistoryIcon color="#3A3A3A" />
                    </TouchableOpacity>
                  </DLOTooltip>
                </View>
              </View>
            </View>
          </View>
        ))}
      </View>

      {/* Modal for success/error messages */}
      <DLOModal
        visible={isModalVisible}
        onClose={handleModalClose}
        title={modalTitle}
        primaryButtonText="OK"
        secondaryButtonText=''
        onPrimaryPress={handleModalClose}
        hideButtons={false}
        showCloseIcon={false}
        width={400}
      >
        <Text style={styles.modalMessage}>
          {modalMessage}
        </Text>
      </DLOModal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    minWidth: '100%',
  },
  container: {
    borderRadius: 8,
    backgroundColor: '#FBFCFE',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '100%',
  },
  
  // Main Header
  mainHeader: {
    backgroundColor: '#005CBC',
    flexDirection: 'row',
    alignItems: 'center',
  },
  percentageVolumeSection: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionSection: {
    flex: 0.12, // Further reduced to match totalActionGroup for consistency
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerSectionText: {
    color: '#FFFFFF',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '700',
    lineHeight: Platform.select({
      web: 20,
      default: 18,
    }),
    textAlign: 'center',
  },
  headerDivider: {
    width: 1,
    backgroundColor: '#FFFFFF',
    alignSelf: 'stretch',
  },

  // Sub Header
  subHeader: {
    backgroundColor: '#F8F9FA',
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  daysGroup: {
    flex: 1,
    flexDirection: 'row',
  },
  dayGroup: {
    flex: 1,
  },
  dayHeader: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EFF5FC',
  },
  dayHeaderText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '600',
    lineHeight: Platform.select({
      web: 18,
      default: 16,
    }),
    textAlign: 'center',
  },
  daySubColumns: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#FBFCFE',
    borderTopColor: '#E0E0E0',
    borderTopWidth: 1,
    borderBottomColor: '#E0E0E0',
    borderBottomWidth: 1,
  },
  subColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  subColumnDivider: {
    width: 1,
    backgroundColor: '#E0E0E0',
    alignSelf: 'stretch',
  },
  subHeaderText: {
    color: '#666666',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '500',
    lineHeight: Platform.select({
      web: 16,
      default: 14,
    }),
    textAlign: 'center',
  },
  dayColumnDivider: {
    width: 1,
    backgroundColor: '#E0E0E0',
    alignSelf: 'stretch',
  },
  totalGroup: {
    flex: 0.2,
  },
  totalActionGroup: {
    flex: 0.12, // Further reduced from 0.16 for even more compact Action column
  },
  totalActionGroupData: {
    position: 'relative',
    bottom: '35%',
  },
  actionHeader: {
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EFF5FC',
  },
  totalHeader: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  totalSubColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  actionSubColumn: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FBFCFE',
  },

  // Data Rows
  dataRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: '#FBFCFE',
  },
  lastDataRow: {
    borderBottomWidth: 0,
  },
  dayInputsContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  inputColumnDivider: {
    width: 1,
    backgroundColor: '#E0E0E0',
    alignSelf: 'stretch',
  },
  inputContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10, // Added 10px horizontal padding
  },
  input: {
    backgroundColor: '#F8F9FA',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 4,
    textAlign: 'center',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '400',
    color: '#3A3A3A',
  },
  defaultInput: {
    backgroundColor: '#D0D0D0',
  },
  overrideInput: {
    backgroundColor: '#FFFFFF',
  },
  totalBadge: {
    borderRadius: 12,
    paddingHorizontal: 6,
    paddingVertical: 2,
    maxWidth: '100%',
    flexShrink: 1,
    borderWidth: 1,
    marginRight: 5,
    minWidth:60,
  },
  totalBadgeGreen: {
    backgroundColor: '#E8F5E8',
    borderColor: '#4CAF50',
  },
  totalBadgeRed: {
    backgroundColor: '#FFEBEE',
    borderColor: '#F44336',
  },
  totalBadgeText: {
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '600',
    textAlign: 'center',
    flexShrink: 1, // Allow text to shrink if needed
  },
  totalBadgeTextGreen: {
    color: '#2E7D32',
  },
  totalBadgeTextRed: {
    color: '#C62828',
  },
  totalSection: {
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  overrideSaveRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap', // Allow wrapping for better responsiveness
    maxWidth: '100%', // Ensure it doesn't exceed container width
  },
  totalLabelText: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontWeight: '600',
    textAlign: 'center',
  },

  // Action Button
  actionButton: {
    width: Platform.select({
      web: 32,
      default: 28,
    }),
    height: Platform.select({
      web: 32,
      default: 28,
    }),
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    backgroundColor: 'transparent',
  },
  modalMessage: {
    fontSize: 16,
    color: '#333',
    textAlign: 'center',
    lineHeight: 24,
    paddingVertical: 16,
  },
});

export default DLOPercentageOfWeeklyDSDVolumeTable;
